import React from 'react';
import { Drawer, Tooltip } from 'antd';
import { Check, CircleHelp } from 'lucide-react';
import { DrawerHeader } from '@/components/DrawerHeader';
import { Button } from '@/components/Button';
import { useDrawerSurface } from '@/hooks';
import type { InterviewTemplate } from '@/services/api';
import { APP_CURRENCY } from '@/utils/constants/currency';
import styles from './PackageDetailsDrawer.module.scss';

interface PackageDetailsDrawerProps {
  template: InterviewTemplate | null;
  onClose: () => void;
  onPractice: () => void;
}

export const PackageDetailsDrawer: React.FC<PackageDetailsDrawerProps> = ({ template, onClose, onPractice }) => {
  const drawerSurface = useDrawerSurface();

  const questionCount = template?.items.length ?? 0;
  const htsQuestionCount = template?.htsQuestionCount ?? 0;
  const interviewCost = template?.interviewCostCredits ?? 0;
  const grantedCredits = template?.unlockIncludedCredits ?? 0;

  return (
    <Drawer
      title={<DrawerHeader title="Package details" onClose={onClose} />}
      closable={false}
      open={Boolean(template)}
      onClose={onClose}
      {...drawerSurface}
      destroyOnHidden
      className={styles.drawer}
    >
      {template && (
        <div className={styles.body}>
          <div className={styles.titleRow}>
            <span className={styles.name}>{template.name}</span>
            {template.htsRequired && <span className={styles.htsBadge}>+HTS</span>}
          </div>

          <p className={styles.description}>{template.description || 'Practice package'}</p>

          <div className={styles.price}>
            <span className={styles.priceDollarSign}>$</span>
            <span className={styles.priceNumber}>{(template.unlockPriceCents / 100).toFixed(2)}</span>
            <span className={styles.priceUnit}>{APP_CURRENCY}</span>
          </div>

          <div className={styles.divider} />

          <div className={styles.checkList}>
            <div className={styles.checkRow}>
              <span className={styles.checkIcon}>
                <Check size={16} />
              </span>
              <span>
                {questionCount} Competency Question{questionCount === 1 ? '' : 's'}
              </span>
            </div>
            {template.htsRequired && (
              <div className={styles.checkRow}>
                <span className={styles.checkIcon}>
                  <Check size={16} />
                </span>
                <span>
                  {htsQuestionCount} HTS Question{htsQuestionCount === 1 ? '' : 's'}
                </span>
              </div>
            )}
            <div className={styles.checkRow}>
              <span className={styles.checkIcon}>
                <Check size={16} />
              </span>
              <span>
                {interviewCost} Credit{interviewCost === 1 ? '' : 's'} per interview
                <Tooltip title="Credits deducted from your balance each time you start a new assessment">
                  <CircleHelp className={styles.checkTooltipIcon} style={{ width: '1em', height: '1em' }} />
                </Tooltip>
              </span>
            </div>
            <div className={styles.checkRow}>
              <span className={styles.checkIcon}>
                <Check size={16} />
              </span>
              <span>
                {grantedCredits} Credit{grantedCredits === 1 ? '' : 's'} granted
                <Tooltip title="Credits you receive when you purchase this package">
                  <CircleHelp className={styles.checkTooltipIcon} style={{ width: '1em', height: '1em' }} />
                </Tooltip>
              </span>
            </div>
          </div>

          <div className={styles.footer}>
            <Button appearance="solid" block onClick={onPractice}>
              Practice
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default PackageDetailsDrawer;
